import { addMinutes } from 'date-fns';

import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { formatTime } from '@apps/web/lib/date_utils';
import {
    type ContactAttemptOutreachStatus,
    type DeliveryMode,
} from '@libraries/typescript/api_client';

type ContactAttemptStatusBadgeProps = {
    readonly status: ContactAttemptOutreachStatus;
    readonly deliveryMode?: DeliveryMode;
    readonly recipientType?: 'caregiver' | 'client';
    readonly isInboundCall?: boolean;
    readonly className?: string;
    readonly communicationDetails?: Record<string, unknown> | null;
    readonly currentShiftEndTime?: string | null;
    readonly showBusyQueuedState?: boolean;
    readonly voiceCallStartedAt?: string | null;
    readonly timezone?: string;
    // Server-computed: this queued voice follow-up is held by the org's
    // quiet hours (ContactAttemptRead.held_by_quiet_hours).
    readonly heldByQuietHours?: boolean;
    // When the current quiet-hours window ends (quiet_hours_hold.resumes_at).
    readonly quietHoursResumesAt?: string | null;
};

// Keep this in sync with the voice dispatch buffer in outreach_tick_activities.py.
const BUSY_CAREGIVER_RESUME_BUFFER_MINUTES = 10;

// Status label + intent for each contact-attempt state. Color comes from the
// intent via the canonical status-token map.
const statusConfig: Record<ContactAttemptOutreachStatus, StatusConfig> = {
    queued: { label: 'Queued', intent: 'neutral', showSpinner: true },
    initiated: { label: 'Sending…', intent: 'info' },
    // To use with only voice
    in_progress: { label: 'Talking…', intent: 'info' },
    reviewing_call: {
        label: 'Reviewing call',
        intent: 'warning',
        showSpinner: true,
    },
    sent: { label: 'Sent', intent: 'info' },
    delivered: { label: 'Delivered', intent: 'positive' },
    completed: { label: 'Completed', intent: 'positive' },
    failed: { label: 'Error', intent: 'negative' },
    no_answer: { label: 'No Answer', intent: 'warning' },
    unreachable: { label: 'Unreachable', intent: 'warning' },
    voicemail_left: { label: 'Voicemail Left', intent: 'positive' },
    unable_to_leave_message: {
        label: 'Unable to Leave Message',
        intent: 'neutral',
    },
    call_disconnected: { label: 'Complete', intent: 'neutral' },
    live_answer: { label: 'Answered', intent: 'positive' },
    manually_notified: { label: 'Manually Notified', intent: 'positive' },
    transferred: { label: 'Transferred', intent: 'warning' },
    scheduled: { label: 'Scheduled', intent: 'info' },
};

function formatFailureReason(reason: string): string {
    const reasonLabels: Record<string, string> = {
        caregiver_currently_on_shift: 'Caregiver is currently on another shift',
        phone_temporarily_unavailable: 'Phone is off, out of coverage, or busy',
        invalid_phone_number: 'Phone number is invalid or unroutable',
        carrier_blocked: 'Call was blocked by the carrier',
        routing_loop: 'Call could not be completed due to a routing loop',
        carrier_temporary_error:
            'The carrier temporarily couldn’t complete the call',
        carrier_rejected: 'The carrier rejected the call',
        message_blocked_by_carrier:
            'The carrier blocked or filtered this text message',
        number_cannot_receive_texts:
            'This phone number can’t receive text messages',
        recipient_opted_out:
            'The caregiver replied STOP and is opted out of texts',
        region_not_enabled: 'Phoebe can’t text this number’s country or region',
        twilio_rejected_destination:
            'Phoebe couldn’t call this phone number — check the number is correct.',
        missing_voice_dispatch_config:
            'Voice calling isn’t set up for this organization yet',
        missing_transfer_number:
            'No transfer number is configured for voice calls',
        caregiver_not_found: 'The caregiver record could no longer be found',
        caregiver_client_do_not_schedule:
            'The caregiver is on this client’s do-not-schedule list',
        missing_phone_contact: 'The caregiver has no phone number on file',
        no_shifts_provided:
            'The shifts were already resolved when the call was attempted',
    };
    return reasonLabels[reason] ?? reason.replace(/_/g, ' ');
}

// Computes the explanatory tooltip for a failed/unreachable badge. Voice
// failures attach a specific `reason`; SMS failures often do not, so fall back
// to a generic, non-alarming message for SMS rather than leaving a bare
// "Error" badge with no explanation. Returns null when no tooltip applies.
function getFailureTooltipText({
    status,
    deliveryMode,
    reason,
}: {
    readonly status: ContactAttemptOutreachStatus;
    readonly deliveryMode?: DeliveryMode;
    readonly reason: unknown;
}): string | null {
    if (status !== 'failed' && status !== 'unreachable') {
        return null;
    }
    if (typeof reason === 'string') {
        return formatFailureReason(reason);
    }
    if (deliveryMode === 'sms') {
        return status === 'unreachable'
            ? 'This phone number couldn’t be reached by text message'
            : 'This text message couldn’t be delivered';
    }
    return null;
}

function getBusyQueuedTooltipText(
    currentShiftEndTime: string | null | undefined,
    timezone: string | undefined,
): string {
    if (!currentShiftEndTime || !timezone) {
        return 'Calls stay queued while the caregiver is on shift and resume about 10 minutes after their shift ends.';
    }

    const resumeAt = addMinutes(
        new Date(currentShiftEndTime),
        BUSY_CAREGIVER_RESUME_BUFFER_MINUTES,
    ).toISOString();

    return `Calls stay queued while the caregiver is on shift and resume at ${formatTime(
        resumeAt,
        timezone,
    )}.`;
}

export function ContactAttemptStatusBadge({
    status,
    deliveryMode,
    recipientType = 'caregiver',
    isInboundCall = false,
    className,
    communicationDetails,
    currentShiftEndTime,
    showBusyQueuedState = false,
    voiceCallStartedAt,
    timezone,
    heldByQuietHours = false,
    quietHoursResumesAt,
}: ContactAttemptStatusBadgeProps) {
    const hasStartedVoiceCall =
        !!voiceCallStartedAt ||
        typeof communicationDetails?.['twilio_call_sid'] === 'string';
    // Quiet hours wins over the busy-caregiver "Waiting" state: overnight it
    // is the gate that actually decides when the call goes out.
    const showQueuedForQuietHours =
        status === 'queued' &&
        deliveryMode === 'voice' &&
        heldByQuietHours &&
        !hasStartedVoiceCall;
    const showQueuedWhileBusy =
        status === 'queued' &&
        deliveryMode === 'voice' &&
        showBusyQueuedState &&
        !!currentShiftEndTime &&
        !hasStartedVoiceCall &&
        !showQueuedForQuietHours;

    // Create a shallow copy to avoid mutating the shared config
    const config = {
        ...statusConfig[
            showQueuedWhileBusy || showQueuedForQuietHours ? 'queued' : status
        ],
    };

    if (status === 'in_progress' && deliveryMode === 'voice' && isInboundCall) {
        config.label = 'Inbound in progress';
    } else if (showQueuedForQuietHours) {
        config.label = 'Paused';
        config.showSpinner = false;
    } else if (showQueuedWhileBusy) {
        config.label = 'Waiting';
    } else if (status === 'sent' && deliveryMode === 'voice') {
        config.label = 'Calling…';
    } else if (status === 'failed' && deliveryMode === 'voice') {
        config.label = 'Call failed';
    } else if (status === 'initiated') {
        if (deliveryMode === 'voice') {
            config.label = 'Calling…';
        }
    }

    if (
        recipientType === 'client' &&
        (status === 'live_answer' || status === 'voicemail_left')
    ) {
        config.label = 'Notified';
    }

    const isNoPhoneFailure =
        status === 'failed' &&
        communicationDetails?.['failure_reason'] === 'no_phone';
    if (isNoPhoneFailure) {
        return (
            <StatusBadge
                status={{ label: 'No phone', intent: 'warning' }}
                className={className}
            />
        );
    }

    const badge = <StatusBadge status={config} className={className} />;

    if (showQueuedForQuietHours) {
        const resumeText =
            quietHoursResumesAt && timezone
                ? `Calling resumes at ${formatTime(quietHoursResumesAt, timezone)}.`
                : 'Calling resumes when quiet hours end.';
        return (
            <Tooltip>
                <TooltipTrigger asChild={true}>{badge}</TooltipTrigger>
                <TooltipContent>
                    <p>
                        {`Phoebe doesn’t call during this organization’s quiet hours. ${resumeText}`}
                    </p>
                </TooltipContent>
            </Tooltip>
        );
    }

    if (showQueuedWhileBusy) {
        return (
            <Tooltip>
                <TooltipTrigger asChild={true}>{badge}</TooltipTrigger>
                <TooltipContent>
                    <p>
                        {getBusyQueuedTooltipText(
                            currentShiftEndTime,
                            timezone,
                        )}
                    </p>
                </TooltipContent>
            </Tooltip>
        );
    }

    // Explain failed/unreachable badges so an SMS "Error" is never unexplained.
    const failureTooltipText = getFailureTooltipText({
        status,
        deliveryMode,
        // Synchronous provider rejections use `failure_reason`; delivery
        // callbacks predate that contract and still write `reason`.
        reason:
            communicationDetails?.['failure_reason'] ??
            communicationDetails?.['reason'],
    });
    if (failureTooltipText !== null) {
        return (
            <Tooltip>
                <TooltipTrigger asChild={true}>{badge}</TooltipTrigger>
                <TooltipContent>
                    <p>{failureTooltipText}</p>
                </TooltipContent>
            </Tooltip>
        );
    }

    return badge;
}
